import React, { Component } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { telemetryService } from "../../../../services/TelemetryService";
import LidarViewSlider from "./LidarViewSlider";
import DateSelector from "../DateSelector";
import { theme } from "../../../../App";

import {
    startOfDay,
    endOfDay,
    isSameDay,
    subMinutes,
    addHours,
    differenceInCalendarDays,
    addMinutes,
    format,
} from "date-fns";
import PQueue from "p-queue/dist";
import Button from "@mui/material/Button";
import { Box } from "@mui/material";

const styles = (theme) => ({
    viewContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
        width: "100%",
    },
    siteGroupTitle: {
        fontWeight: "700",
        fontSize: "1.2rem",
        padding: "1rem 0",
    },
    slider: {
        height: "15%",
        minHeight: "200px",
        width: "80%",
        paddingTop: "1rem",
        [theme.breakpoints.down("lg")]: {
            width: "95%",
        },
    },
    canvasContainer: {
        width: "75%",
        height: "55%",
        margin: "1rem 0",
        [theme.breakpoints.down("lg")]: {
            width: "95%",
        },
    },
    canvas: {
        width: "100%",
        height: "100%",
    },
    currentDttm: {
        padding: "1rem 0",
        fontWeight: 600,
    },
    headerContainer: {
        width: "90%",
        marginTop: "1rem",
        display: "flex",
        [theme.breakpoints.down("lg")]: {
            flexDirection: "column",
        },
    },
    dateSelectorContainer: {
        margin: "auto",
    },
});

const queue = new PQueue({ concurrency: 36 });

const vertexShader = `
    // Attributes per vertex: position
    attribute vec4 aPosition;
    uniform mat4   matrix_viewProjection;
    uniform mat4   matrix_model;
    // position of the camera
    uniform vec3 cameraPos;
    // Color to fragment program
    varying vec4 outColor;
    void main(void)
    {
        // Transform the geometry
        mat4 modelViewProj = matrix_viewProjection * matrix_model;
        gl_Position = modelViewProj * aPosition;
        // vertex in world space
        //vec4 vertexWorld = matrix_model * aPosition;
        // point sprite size depends on its distance to camera
        //float dist = 5.0 - length(vertexWorld.xyz - cameraPos);
        gl_PointSize = 4.0; //clamp(dist * 2.0 - 1.0, 1.0, 15.0);
        // color depends on position of particle
        outColor = vec4(0, 0.1, .6, 1);
    }`;

const fragmentShader = `
    precision lowp float;
    varying vec4 outColor;
    void main(void)
    {
        // color supplied by vertex shader
        gl_FragColor = outColor;
    }`;

let pc = null;

// manages PlayCanvas plugins
const pluginManifest = [
    {
        type: "script",
        url: "/scripts/orbit-camera.js",
    },
];

class LidarView extends Component {
    constructor(props) {
        super(props);

        this.state = {
            startDateTime: startOfDay(new Date()),
            endDateTime: new Date(),
            startOfData: null,
            dateError: false,
            errorText: "",
            ptCloudData: [],
            selectedDttm: null,
            openStart: false,
            openEnd: false,
            loading: true,
            siteGroupIds: "",
            updateSelectedDttm: false,
            cloudPointStartDttm: new Date(),
        };

        this.points = null;
        this.mesh = null;
        this.cloudPointData = {};
        this.app = null;
        this.handleResize = this.handleResize.bind(this);

        this.classes = styles(theme);
    }

    handleResize() {
        if (this.app) {
            this.app.resizeCanvas();
        }
    }

    componentDidMount() {
        window.scrollTo(0, 0);

        let { startDateTime, endDateTime } = this.state;

        // FIXME: Switch to normal routing parameters
        this.id = this.props.location.pathname.split("/")[4];
        this.playCanvasScript = this.loadScript("https://code.playcanvas.com/playcanvas-1.56.0.min.js");

        let utcOffset = startDateTime.getTimezoneOffset();
        let startOfDayUTC = subMinutes(startDateTime, utcOffset);

        telemetryService.getEdgeMessages(this.id, "v3", false, startOfDayUTC, endDateTime, "", true).then((res) => {
            if (res?.data) {
                this.setPointCloud(res.data.pointCloud);
                this.fetchPointCloudByKey(res.data.keys);
            }

            this.setState({ loading: false });
        });
    }

    async componentDidUpdate(_, prevState) {
        let { updateSelectedDttm, selectedDttm } = this.state;
        if (prevState.updateSelectedDttm !== this.state.updateSelectedDttm && updateSelectedDttm && selectedDttm) {
            this.setState({ updateSelectedDttm: false });
            let values;
            let formattedDate = selectedDttm.split(":").slice(0, 2).join(":");
            let selectedDataSet = this.cloudPointData[formattedDate];

            if (selectedDataSet) {
                values = selectedDataSet;
            } else {
                let startDttm = new Date(selectedDttm);
                let endDttm = new Date(selectedDttm);

                startDttm = subMinutes(startDttm, 1).toISOString();
                endDttm = addMinutes(endDttm, 1).toISOString();

                let result = await telemetryService.getEdgeMessages(this.id, "v3", true, startDttm, endDttm);

                values = result?.data[0]?.compressedScanOutput;

                selectedDataSet = values;
            }

            this.setEdgeMessages(values);
            this.setState({ loading: false });
        }
    }

    componentWillUnmount() {
        document.body.removeChild(this.playCanvasScript);
        window.removeEventListener("resize", this.handleResize);
        if (this.app) {
            this.app.destroy();
        }
    }

    /**
     *
     * @param {PlayCanvas} pc - playcavas instance
     * @param {*} app - Playcanvas app instance
     */
    createShader = (pc, app) => {
        // Create the shader definition and shader from the vertex and fragment shaders
        let shaderDefinition = {
            attributes: {
                aPosition: pc.SEMANTIC_POSITION,
            },
            vshader: vertexShader,
            fshader: fragmentShader,
        };

        let shader = new pc.Shader(app.graphicsDevice, shaderDefinition);

        return shader;
    };

    /** Loads PlayCanvas specific plugins - these must be loaded/managed by the engine */
    loadPlugins = (app) => {
        // Load all assets and then run the example
        pluginManifest.forEach(function (entry) {
            app.assets.loadFromUrl(entry.url, entry.type, function (err, asset) {
                if (!err && asset) {
                    entry.asset = asset;
                }
            });
        });
    };

    // This fires when the playcanvas script loads  and the pc (PlayCanvas) library is ready
    scriptLoad = (sc) => {
        if (sc.target.src.indexOf("playcanvas-") < 0) {
            return;
        }

        pc = window.pc;

        const canvas = document.getElementById("application");

        this.app = new pc.Application(canvas, {
            mouse: new pc.Mouse(canvas),
            touch: new pc.TouchDevice(canvas),
        });

        this.loadPlugins(this.app);

        this.app.setCanvasFillMode(pc.FILLMODE_NONE);
        this.app.setCanvasResolution(pc.RESOLUTION_AUTO);

        // ensure canvas is resized when window changes size
        window.addEventListener("resize", this.handleResize);

        // create box entity
        const box = new pc.Entity();

        box.addComponent("model", {
            type: "box",
        });
        app.root.addChild(box);
        box.setPosition(0, 0, 0);

        box.setLocalScale(100, 0.1, 100);

        // Add model of points
        this.mesh = new pc.Mesh(app.graphicsDevice);
        let positions = [];
        this.mesh.setPositions(positions);
        this.mesh.update();
        this.mesh.aabb = new pc.BoundingBox(new pc.Vec3(0, 0, 0), new pc.Vec3(100, 100, 100));

        // Create the mesh instance
        const node = new pc.GraphNode();

        node.rotate(45, 0, -180);

        let shader = this.createShader(pc, app);

        // Create a new material with the new shader
        let material = new pc.Material();

        material.shader = shader;
        material.blendType = pc.BLEND_BASIC;

        const meshInstance = new pc.MeshInstance(node, this.mesh, material);
        // Create a model and add the mesh instance to it
        let model = new pc.Model();
        model.graph = node;
        model.meshInstances = [meshInstance];

        meshInstance.material = material;

        // Set all mesh points to render as primitive points
        meshInstance.mesh.primitive.forEach(function (primitive) {
            primitive.type = pc.PRIMITIVE_POINTS;
        });

        // Create Entity and add it to the scene
        let entity = new pc.Entity();
        app.root.addChild(entity);

        // Add a model compoonent
        app.systems.model.addComponent(entity, {
            type: "asset",
        });

        entity.model.model = model;

        // create camera entity
        const camera = new pc.Entity();

        camera.addComponent("camera", {
            nearClip: 0.001,
            clearColor: new pc.Color(0.98, 0.98, 0.98), // Match background in app
        });

        camera.translate(0, 0, 10);

        app.root.addChild(camera);

        camera.addComponent("script");

        app.assets.loadFromUrl("/scripts/orbit-camera.js", "script", function () {
            camera.addComponent("script");
            camera.script.create("orbitCamera", {
                attributes: {
                    inertiaFactor: 0.2,
                    focusEntity: box,
                },
            });
            camera.script.create("orbitCameraInputMouse");
            camera.script.create("orbitCameraInputTouch");
        });

        camera.setPosition(0, 0, 90);

        // create directional light entity
        const light = new pc.Entity();
        light.addComponent("light");
        app.root.addChild(light);
        light.setEulerAngles(45, 0, 0);

        app.start();
    };

    /**
     * Reads the device Id from the
     */
    setEdgeMessages = async (scanOutput) => {
        if (this.id && scanOutput?.length) {
            if (scanOutput.length > 0) {
                this.points = scanOutput;
            }

            if (this.points) {
                this.updateMesh(this.points);
            }
        } else if (this.id && !scanOutput) {
            this.points = [];
            this.updateMesh(this.points);
        }
    };

    setPointCloud = async (result) => {
        let siteGroupIds = "";

        if (this.id && result?.length) {
            let { ptCloudData } = this.state;
            let data = result.map((ele) => [ele.create_dttm, ele.compressed_count]);
            let updatedPtCloudData = [...ptCloudData, ...data];
            let siteIds = result[0].site_group_ids;
            let siteGroupName = result[0].site_group_name ?? "";

            if (siteIds?.length) {
                siteIds.sort((a, b) => a - b);
                siteGroupIds = siteIds.join(", ");
            }

            updatedPtCloudData = updatedPtCloudData.sort(this.sortPointCloudData);
            let lastDttm = updatedPtCloudData[updatedPtCloudData.length - 1][0];

            this.setState({
                ptCloudData: updatedPtCloudData,
                siteGroupIds,
                selectedDttm: lastDttm,
                siteGroupName,
            });
        }
    };

    sortPointCloudData = (a, b) => {
        let dateA = new Date(a[0]);
        let dateB = new Date(b[0]);

        if (dateA === dateB) {
            return 0;
        } else {
            return dateA < dateB ? -1 : 1;
        }
    };

    updateMesh = (points) => {
        if (this.mesh) {
            let positions = [];

            // Z is up with the lidar view, but Z is out (towards the viewer) in a typeical WebGL 3D view
            for (let point of points) {
                positions.push(-point.x);
                positions.push(point.z);
                positions.push(point.y);
            }
            this.mesh.clear();

            this.mesh.setPositions(positions);
            this.mesh.update(pc.PRIMITIVE_POINTS, false);
        }
    };

    loadScript = (url) => {
        let script = document.createElement("script");

        script.src = url; //"https://code.playcanvas.com/playcanvas-stable.min.js";
        script.async = true;

        script.onload = this.scriptLoad;

        document.body.appendChild(script);

        return script;
    };

    updateSelectedDttm = (selectedDttm) => {
        this.setState({ selectedDttm, updateSelectedDttm: true });
    };

    handleStartDateChange = (date) => {
        let startOfData = startOfDay(date);

        this.setState({
            openStart: false,
            openEnd: true,
            startDateTime: startOfData,
        });
    };

    handleEndDateChange = (date) => {
        let endOfData = endOfDay(date);
        let errorText;
        let dateError;
        let { startDateTime, endDateTime, startOfData } = this.state;

        if (startDateTime > date) {
            errorText = "End Date cannot be earlier than Start Date.";
            dateError = true;
            return;
        }

        if (isSameDay(endOfData, endDateTime)) {
            endOfData = endDateTime;
        }

        let dataStart = new Date(startOfData);
        let utcOffset = startDateTime.getTimezoneOffset();
        dataStart = addMinutes(dataStart, utcOffset);

        if (!isSameDay(startDateTime, date) && differenceInCalendarDays(dataStart, startDateTime) > 1) {
            errorText = "Data request cannot exceed 1 day.";
            dateError = true;
        } else {
            errorText = "";
            dateError = false;
        }

        this.setState({ openEnd: false, endDateTime: endOfData, errorText, dateError });
    };

    handleOpen = (bool) => {
        this.setState({ openStart: bool });
    };

    handleEnd = (bool) => {
        this.setState({ openEnd: bool });
    };

    handleDataFetch = (startDateTime, endDateTime, loading) => {
        if (!loading) {
            if (startDateTime > endDateTime) {
                this.setState({
                    errorText: "End Date cannot be earlier than Start Date.",
                    dateError: true,
                });
                return;
            }

            if (!isSameDay(startDateTime, endDateTime) && differenceInCalendarDays(endDateTime, startDateTime) > 1) {
                this.setState({ errorText: "Data request cannot exceed 1 day.", dateError: true });
                return;
            }

            this.setState({ loading: true, errorText: "", dateError: false });
            this.fetchData(startDateTime, endDateTime);
        }
    };

    fetchData = (startDateTime, endDateTime) => {
        let utcOffset = startDateTime.getTimezoneOffset();
        startDateTime = subMinutes(startDateTime, utcOffset);
        endDateTime = subMinutes(endDateTime, utcOffset);

        telemetryService
            .getEdgeMessages(this.id, "v3", false, startDateTime, endDateTime, 10, false, false)
            .then((res) => {
                if (res) {
                    this.setPointCloud(res.data.pointCloud);
                    this.fetchPointCloudByKey(res.data.keys);
                }

                this.setState({ loading: false });
            });
    };

    fetchAllPointCloudData = () => {
        let { startDateTime, endDateTime } = this.state;
        let startDttm = startDateTime;
        let endDttm = endDateTime;

        for (let i = startDttm; i < endDttm; i = addHours(i, 1)) {
            let next = addHours(i, 1);

            queue.add(() =>
                telemetryService.getEdgeMessages(this.id, "v3", true, i, next).then((records) => {
                    let data = records.data;
                    if (data.length) {
                        data.forEach((d) => {
                            this.cloudPointData[d.dttm] = d.compressedScanOutput;
                        });
                    }
                })
            );
        }
    };

    fetchPointCloudByKey = async (data) => {
        if (data) {
            let dataStart = new Date();
            let deviceName = "edge" + this.id;
            let year = dataStart.getFullYear();
            let month = (dataStart.getMonth() < 10 ? "0" : "") + (dataStart.getMonth() + 1);
            let day = (dataStart.getDate() < 10 ? "0" : "") + dataStart.getDate();

            for (let i = 0; i < data.length - 1; i += 50) {
                let fileNames = data.slice(i, i + 50).reduce((acc, el) => {
                    let key = el.Key.split("_");
                    let suffix = key[key.length - 1];

                    return acc + suffix + "@";
                }, "");

                let dataBody = {
                    deviceName,
                    year,
                    month,
                    day,
                };

                dataBody["fileNames"] = fileNames;

                await queue.add(() =>
                    telemetryService
                        .getEdgeMessages(this.id, "v3", true, null, null, "", "", false, false, dataBody)
                        .then((records) => {
                            if (records) {
                                let data = records.data;

                                data.forEach((d) => {
                                    if (d) {
                                        if (this.isJSONString(d)) {
                                            d = JSON.parse(d);
                                        }

                                        let date = new Date(d.timestamp);
                                        let utcOffset = date.getTimezoneOffset();
                                        let utcDate = subMinutes(date, utcOffset);
                                        let dttm = utcDate.toISOString();
                                        let formattedDate = dttm.split(":").slice(0, 2).join(":");
                                        let points = d.compressed_scan_output?.points;

                                        if (points) {
                                            this.cloudPointData[formattedDate] = d.compressed_scan_output?.points;
                                        }

                                        if (date < this.state.cloudPointStartDttm) {
                                            this.setState({ cloudPointStartDttm: date });
                                        }
                                    }
                                });
                            }
                        })
                );

                queue.on("idle", () => {
                    let utcOffset = this.state.cloudPointStartDttm.getTimezoneOffset();
                    let utcDate = subMinutes(this.state.cloudPointStartDttm, utcOffset);
                    let startOfData = utcDate.toISOString();

                    this.setState({ startOfData, updateSelectedDttm: true });
                });
            }
        }
    };

    reversePtCloudData = (data) => {
        return data.sort((a, b) => (a.dttm > b.dttm ? 1 : -1));
    };

    handleGoBack = () => {
        this.props.navigate(-1);
    };

    isJSONString(d) {
        try {
            if (typeof JSON.parse(d) == "object") return true;
        } catch (e) {
            console.error(e);
            return false;
        }
        return false;
    }

    render() {
        let {
            ptCloudData,
            openStart,
            openEnd,
            loading,
            startDateTime,
            endDateTime,
            siteGroupIds,
            dateError,
            errorText,
            selectedDttm,
            siteGroupName,
        } = this.state;
        let formattedDate;

        if (selectedDttm) {
            let date = new Date(selectedDttm);
            let utcOffset = date.getTimezoneOffset();
            let utcDate = addMinutes(date, utcOffset);
            formattedDate = format(utcDate, "yyyy-MM-dd HH:mm:ss");
            formattedDate += " (UTC)";
        }

        return (
            <Box component="div" sx={this.classes.viewContainer}>
                <Box component="div" sx={this.classes.headerContainer}>
                    <div>
                        <Button onClick={this.handleGoBack} variant="contained" color="primary" size="small">
                            Back
                        </Button>
                    </div>
                    <Box component="div" sx={this.classes.dateSelectorContainer}>
                        <DateSelector
                            handleStartDateChange={this.handleStartDateChange}
                            handleEndDateChange={this.handleEndDateChange}
                            setOpenStart={this.handleOpen}
                            setOpenEnd={this.handleEnd}
                            openStart={openStart}
                            openEnd={openEnd}
                            loading={loading}
                            startDateTime={startDateTime}
                            endDateTime={endDateTime}
                            handleDataFetch={this.handleDataFetch}
                            dateError={dateError}
                            errorText={errorText}
                            style={{ padding: ".5rem" }}
                        />
                    </Box>
                </Box>

                {!loading && siteGroupIds ? (
                    <Box component="div" sx={this.classes.siteGroupTitle}>
                        Site Group: {siteGroupName} [{siteGroupIds}]
                    </Box>
                ) : null}

                {!loading && formattedDate ? (
                    <Box component="div" sx={this.classes.currentDttm}>
                        {formattedDate}
                    </Box>
                ) : null}

                {!loading && ptCloudData.length ? (
                    <Box component="div" sx={this.classes.slider}>
                        <LidarViewSlider data={ptCloudData} updateSelectedDttm={this.updateSelectedDttm} />
                    </Box>
                ) : null}

                <Box
                    component="div"
                    sx={this.classes.canvasContainer}
                    style={{ visibility: loading ? "hidden" : "visible" }}
                >
                    <Box component="canvas" sx={this.classes.canvas} id="application"></Box>
                </Box>
            </Box>
        );
    }
}

const LidarViewWrapper = () => {
    const navigate = useNavigate();
    const location = useLocation();

    return <LidarView navigate={navigate} location={location} />;
};

export default LidarViewWrapper;
